import * as React from 'react';
import { NavMain } from '@/components/custom/sidebar/nav-main';
import { NavUser } from '@/components/custom/sidebar/nav-user';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/ui/sidebar';
import { WorkspaceSwitcher } from '@/components/custom/sidebar/workspace-switcher';
import { ROUTES } from '@/utils/constants';
import { RootState } from '@/store/reducers';
import { useSelector } from 'react-redux';
import useInIframe from '@/hooks/useInIframe';
import MyConversationsIcon from '../../../public/icons/my-conversations-icon.svg';
import SharedWithMeIcon from '../../../public/icons/shared-with-me-icon.svg';
import SetupIcon from '../../../public/icons/setup-icon.svg';
import AllConversationsIcon from '../../../public/icons/all-conversations-icon.svg';
import ConversationInsightsIcon from '../../../public/icons/conversation-insights-icon.svg';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  branding: {
    headerText: string;
  };
  activeWorkspace: string;
  handleSelectWorkspace: Function;
  workspaceAdminDetails: any;
  selectedWorkspace: {
    created_at: string | null;
    created_by: string | null;
    id: number;
    name: string;
    organisation_id: number;
    subscription_tier: string;
    tags: string[] | [];
    seats:number|null;
    parameter_limit: number;
    preferences?: { [key:string]: any }
  }[]
  | [];
}

export function AppSidebar({
  branding,
  activeWorkspace,
  handleSelectWorkspace,
  workspaceAdminDetails,
  selectedWorkspace,
  ...props
}: AppSidebarProps) {
  const inIframe = useInIframe();
  const { isAdmin, isManager, user, metabaseDashboards, metabaseUnavailable } = useSelector(
    (state: RootState) => state.common,
  );

  const isPartner = Boolean(inIframe)
  const isZipteamsUser = user?.email?.includes("zipteams@") || user?.email?.includes("@zipteams");

  const showNewCoversationInsights = Boolean(isZipteamsUser && user?.organisation?.feature_flags?.show_conv_insights_new);

  const navItems = React.useMemo(
    () => [
      { title: 'Setup', url: '/workspace', icon: SetupIcon },
      ...((isAdmin || isManager) && showNewCoversationInsights
        ? [
            {
              title: 'Conversations Insights',
              url: `/${ROUTES.CONVERSATIONS_INSIGHTS}`,
              icon: ConversationInsightsIcon,
            },
          ]
        : []),
      ...((isAdmin || isManager) && !showNewCoversationInsights && metabaseDashboards && metabaseDashboards?.length && !metabaseUnavailable
        ? [
            {
              title: 'Conversations Insights',
              url: `/${ROUTES.CONVERSATIONS_DASHBOARD}`,
              icon: ConversationInsightsIcon,
            },
          ]
        : []),
      {
        title: 'My Conversations',
        url: `/${ROUTES.DASHBOARD}`,
        icon: MyConversationsIcon,
      },
      ...(!isPartner ? [{
        title: 'Shared With Me',
        url: `/${ROUTES.SHARED_WITH_YOU}`,
        icon: SharedWithMeIcon,
      }] : []),
      ...(isAdmin || isManager
        ? [
            {
              title: 'All Conversations',
              url: `/${ROUTES.DASHBOARD}/admin`,
              icon: AllConversationsIcon,
            },
          ]
        : []),
    ],
    [isAdmin, isManager, metabaseDashboards, metabaseUnavailable, isPartner, showNewCoversationInsights],
  );

  return (
    <Sidebar collapsible="icon" className='bg-white' {...props}>
      <SidebarHeader>
        <WorkspaceSwitcher
          branding={branding}
          activeWorkspace={activeWorkspace}
          handleSelectWorkspace={handleSelectWorkspace}
        />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navItems} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} workspaceAdminDetails={workspaceAdminDetails} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
